import { mediaQuery } from '../utils/mediaQuery';
import { $Q, $Qll } from '../utils/querySelector';
import { stickyElement } from '../utils/sticky';
import { toggleDataActive } from '../utils/toggleDataset';
import { currentLang } from '../utils/getLanguage';


/**
 * @author Daniel Santos <daniel.santos@gradiweb.com>
 */
class Header {
  /**
   * Assign header component
   * @author Daniel Santos <daniel.santos@gradiweb.com>
   */
  constructor() {
    this.headerElement = $Q('#header');

    if (this.headerElement) {
      this._init();
    }
  }

  /**
   * Init header functionalities
   * @author Daniel Santos <daniel.santos@gradiweb.com>
   */
  _init() {
    mediaQuery('(min-width: 992px)', this._listenerHover.bind(this));
    mediaQuery('(max-width: 991px)', this._burgerMenu.bind(this));
    this.stickyHeader();
  }

  /**
   * Validate submenus quantity for item header
   * @param {node} - Paren node
   * @author Daniel Santos <daniel.santos@gradiweb.com>
   * @return {boolean} true if children are greater than 1 and false if children are less
   */
  _validateSubmenusQty(item) {
    const submenu = $Q(
      '.js-submenu-component .menu-component__submenu-wrapper',
      item
    );
    const children = [...submenu.children].slice();

    if (children[0].classList.contains('caret')) {
      return {
        caret: children.length > 2,
        qtyElements: children.length > 2 && children.length - 1,
      };
    } else {
      return {
        qtyElements: children.length,
      };
    }
  }

  /**
   * Callback of mouseover event - method: this._listenerHover
   * @param {Node} item - Node target
   * @author Daniel Santos <daniel.santos@gradiweb.com>
   */
  _cbMouseOverMenu(item) {
    const header = this.headerElement;

    if (!header.classList.contains('isHover')) {
      header.classList.add('isHover');
      item.classList.add('isHover');

      if (
        this._validateSubmenusQty(item).caret &&
        !header.classList.contains(
          `any-${this._validateSubmenusQty(item).qtyElements}`
        )
      ) {
        header.classList.add(
          `any-${this._validateSubmenusQty(item).qtyElements}`
        );

        if (this._validateSubmenusQty(item).qtyElements != 0) {
          $Q(
            '.js-submenu-component',
            item
          ).style.width = `100%`;
        }
      } else if (!header.classList.contains('oneElements')) {
        header.classList.add('oneElements');
        $Q('.js-submenu-component', item).style.width = `100%`;
      }
    }
  }

  /**
   * Callback of mouseout event - method: this._listenerHover
   * @param {Node} item - Node target
   * @author Daniel Santos <daniel.santos@gradiweb.com>
   */
  _cbMouseOutMenu(item) {
    const header = this.headerElement;

    if (header.classList.contains('isHover')) {
      header.classList.remove('isHover');
      item.classList.remove('isHover');
    }

    if (
      header.classList.contains(
        `any-${this._validateSubmenusQty(item).qtyElements}`
      )
    ) {
      header.classList.remove(
        `any-${this._validateSubmenusQty(item).qtyElements}`
      );
    } else if (header.classList.contains('oneElements')) {
      header.classList.remove('oneElements');
    }
  }

  /**
   * Init menu with css class
   * Valiate how many menus has each item of the header and add class
   * @author Daniel Santos <daniel.santos@gradiweb.com>
   */
  _initMenus(item) {
    //Init menus with one submenu
    if (!this._validateSubmenusQty(item).caret) {
      if (!this._validateSubmenusQty(item).qtyElements) {
        item.classList.add('one-menu');
      }
    } else {
      if (this._validateSubmenusQty(item).qtyElements === 2) {
        item.classList.add('two-menu');
      }

      if (this._validateSubmenusQty(item).qtyElements === 3) {
        item.classList.add('three-menu');
      }
    }
  }

  /**
   * Listener hover is menu items to: Open submenu and change background color
   * @author Daniel Santos <daniel.santos@gradiweb.com>
   */
  _listenerHover() {
    const items = $Qll('.js-menu-dropdown', this.headerElement);
    const itemsSingle = $Qll('.js-menu-single', this.headerElement);

    items &&
      items.forEach((item) => {
        //Init menus with one submenu
        this._initMenus(item);

        //Init hover menus with submenus
        item.addEventListener(
          'mouseover',
          this._cbMouseOverMenu.bind(this, item)
        );
        item.addEventListener(
          'mouseout',
          this._cbMouseOutMenu.bind(this, item)
        );

        item.addEventListener(
          'focusin',
          this._cbMouseOverMenu.bind(this, item)
        );

        item.addEventListener('focusout', () => {
          requestAnimationFrame(() => {
            if (!item.contains(document.activeElement)) {
              this._cbMouseOutMenu(item);
            }
          });
        });
      });

    //Init hover of the header items that no has menus
    itemsSingle &&
      itemsSingle.forEach((itemSingle) => {
        this.mouseoverCssClass(itemSingle);
        this.mouseoutCssClass(itemSingle);
      });

    //Init hove of the header
    this.mouseoverCssClass(this.headerElement);
    this.mouseoutCssClass(this.headerElement);
  }

  /**
   * Add class when mouse is over of the element
   * @author Daniel Santos <daniel.santos@gradiweb.com>
   */
  mouseoverCssClass(target, cssClass = 'isHover') {
    const addClass = () => {
      if (!target.classList.contains(cssClass)) {
        target.classList.add(cssClass);
      }
    };

    target.addEventListener('mouseover', addClass);
    target.addEventListener('focusin', addClass);
  }

  /**
   * Remove class when mouse is out of the element
   * @author Daniel Santos <daniel.santos@gradiweb.com>
   */
  mouseoutCssClass(target, cssClass = 'isHover') {
    const removeClass = () => {
      if (target.classList.contains(cssClass)) {
        target.classList.remove(cssClass);
      }
    };

    target.addEventListener('mouseout', removeClass);
    target.addEventListener('focusout', () => {
      requestAnimationFrame(() => {
        if (!target.contains(document.activeElement)) {
          removeClass();
        }
      });
    });
  }

  /**
   * Open menu in mobile
   * @author Daniel Santos <daniel.santos@gradiweb.com>
   */
  _burgerMenu() {
    toggleDataActive('#js-burger-menu button', '#js-burger-menu');
    toggleDataActive('#js-burger-menu button', '#header');

    mediaQuery('(max-width: 991px)', this._openSubmenusMobile.bind(this));
    // mediaQuery('(max-width: 991px)', this._dropdownSubmenu.bind(this));
  }

  /**
   * Validate dataset active
   * @param node} item
   * @returns {null} return if dataset is false
   * @author Daniel Santos <daniel.santos@gradiweb.com>
   */
  _cbDataToggle(item) {
    const dataset = item.dataset.active;
    const isActive = dataset != 'true' ? false : true;

    if (!dataset) return null;

    if (!isActive) {
      item.dataset.active = 'true';
    } else {
      item.dataset.active = 'false';
    }
  }

  /**
   * Open submenu in mobile
   * @author Daniel Santos <daniel.santos@gradiweb.com>
   */
  _openSubmenusMobile() {
    $Qll(
      '#js-menu-component .js-menu-component-item.js-menu-dropdown .menu-dropdown'
    ).forEach((item) => {
      const toggleMenu = (e) => {
        e.preventDefault();

        this._cbDataToggle(item.parentNode);
        this._closeWeglot(item.parentNode);
      };

      item.addEventListener('click', toggleMenu);

      item.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          toggleMenu(e);
        }
      });
    });
  }

  /**
   * Closed the Weglot menu when open another menu
   * @param {Node} parentItem - Parent node of the items menu
   * @author Daniel Santos <daniel.santos@gradiweb.com>
   */
  _closeWeglot(parentItem) {
    const itemWeglot = $Q('#js-menu-component .custom-weglot aside');

    if (itemWeglot) {
      if (parentItem.dataset.active) {
        if (
          !itemWeglot.classList.contains('closed') &&
          itemWeglot.getAttribute('aria-expanded') === 'true'
        ) {
          itemWeglot.classList.add('closed');
          itemWeglot.setAttribute('aria-expanded', false);
        }
      }
    }
  }

  /**
   * Sticky header
   */
  stickyHeader() {
    if (this.headerElement.dataset.productpage != 'product') {
      stickyElement({
        elementSticky: this.headerElement,
        pixelsToSticky: 120,
        cssClass: 'isSticky',
      });
    }
  }

  /**
   * Dropdown submenu, created custom for mobile
   * @author Daniel Santos <daniel.santos@gradiweb.com>
   */
  _dropdownSubmenu() {
    $Qll('.js-submenu-list-component').forEach((item, i, array) => {
      const title = $Q('.js-submenu-list-title', item);
      if (!title) return;

      title.setAttribute('tabindex', '0');
      title.setAttribute('role', 'button');

      const toggleSubmenu = (e) => {
        e.preventDefault();

        array.forEach((el) => {
          const titleSecond = $Q('.js-submenu-list-title', el);

          if (titleSecond !== title) {
            if (titleSecond.dataset.active === 'true') {
              titleSecond.dataset.active = false;
              titleSecond.setAttribute('aria-expanded', 'false');
            }
          }
        });

        this._cbDataToggle(title);

        const isActive = title.dataset.active === 'true';

        title.setAttribute(
          'aria-expanded',
          isActive ? 'true' : 'false'
        );
      };

      item.addEventListener('click', toggleSubmenu);

      item.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          toggleSubmenu(e);
        }
      });
    });
  }
}

/*
  Author: Lina Arroyo <lina.arroyo@gradiweb.com>

  Place menu component depending on the header height
 */


let burgerIcon = $Q("#js-burger-menu");

burgerIcon.addEventListener('click', function () {
  let topbarHeight = $Q("#js-topbar-component").offsetHeight;
  let menuComponent = $Q("#js-menu-component");
  let totalHeight = 40 + topbarHeight + "px";
  menuComponent.style.top = `${totalHeight}`;

});

export { Header };
